import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { UrlEnum } from '../../enums/url.enum';
import { HttpRequestService } from '../http-request/http-request.service';
import type {
  AuditLogListResult,
  AuditLogDetailsResult,
  AuditLogQueryParams,
  AuditLogDetailsQueryParams,
} from '../../interfaces/audit-log/audit-log.interface';

@Injectable({
  providedIn: 'root',
})
export class AuditLogService {
  private readonly auditApi = UrlEnum.OTITHI_AUTH + '/audit';

  constructor(private readonly httpRequestService: HttpRequestService) {}

  /** Fetches entity options for audit log filter (GET /audit/applicable-entities). Response: { status, resultset: { value, label }[] }. */
  getApplicableEntities(): Observable<{ value: string; label: string }[]> {
    return this.httpRequestService
      .get<{
        status: string;
        resultset: { value: string; label: string }[];
      }>(`${this.auditApi}/applicable-entities`)
      .pipe(
        map((res) => {
          if (res?.status === 'ok' && Array.isArray(res?.resultset))
            return res.resultset;
          return [];
        }),
      );
  }

  getLogs(query?: AuditLogQueryParams): Observable<AuditLogListResult> {
    let params = new HttpParams();
    if (query) {
      if (query.action) params = params.set('action', query.action);
      if (query.entity) params = params.set('entity', query.entity);
      if (query.propertyId) params = params.set('propertyId', query.propertyId);
      if (query.actorUserId)
        params = params.set('actorUserId', query.actorUserId);
      if (query.dateFrom) params = params.set('dateFrom', query.dateFrom);
      if (query.dateTo) params = params.set('dateTo', query.dateTo);
      if (query.search) params = params.set('search', query.search);
      if (query.page != null) params = params.set('page', String(query.page));
      if (query.limit != null)
        params = params.set('limit', String(query.limit));
    }
    return this.httpRequestService
      .get<{
        status: string;
        result: AuditLogListResult;
      }>(`${this.auditApi}/logs`, { params })
      .pipe(
        map((res) => {
          if (res?.status === 'ok' && res?.result) return res.result;
          return { data: [], total: 0, page: 1, limit: 20, totalPages: 0 };
        }),
      );
  }

  getLogDetails(
    query: AuditLogDetailsQueryParams,
  ): Observable<AuditLogDetailsResult> {
    let params = new HttpParams()
      .set('entity', query.entity)
      .set('entityId', query.entityId);
    if (query.action) params = params.set('action', query.action);
    if (query.dateFrom) params = params.set('dateFrom', query.dateFrom);
    if (query.dateTo) params = params.set('dateTo', query.dateTo);
    if (query.page != null) params = params.set('page', String(query.page));
    if (query.limit != null) params = params.set('limit', String(query.limit));

    return this.httpRequestService
      .get<{
        status: string;
        result: AuditLogDetailsResult;
      }>(`${this.auditApi}/log-details`, { params })
      .pipe(
        map((res) => {
          if (res?.status === 'ok' && res?.result) return res.result;
          return { data: [], total: 0, page: 1, limit: 50, totalPages: 0 };
        }),
      );
  }
}
